import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, Output, EventEmitter, HostListener, ChangeDetectorRef, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToasterService } from '../../../services/toaster.service';
import { ExportLoaderService } from '../../../services/export-loader.service';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, DateRangePickerComponent, DateRange } from '../primeng';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { FormatService } from '../../services/format.service';
import {
  ConversationService,
  canOpenConversation,
  isAccessStillActive,
} from '../../services/conversation.service';
import { AclApdApiService, AuthApiService, istToUtc } from '@angular-workspace/services';
import { FilterPanelComponent, FilterField } from '../filter-panel/filter-panel.component';
import {
  InlineActionDropdownMenuController,
  openInlineTableActionDropdown,
} from '../../utils/action-dropdown-overlay.sync';
import { SingleRowExpansionState } from '../../utils/single-row-expansion.util';
import { displayValueOrDash } from '../../utils/display-value.util';
import { WorkspaceContextService } from '../../../services/workspace-context.service';

export interface AssetRequestData {
  assetName: string;
  assetType: string;
  requestedBy: string;
  requestType: string;
  /** Owner display name from API `owner.ownerFirstName` + `owner.ownerLastName`. */
  assetOwner: string;
  organisationName: string;
  /** Shown as Requested At; sourced from API `createdAt` / `created_at`. */
  createdAt: string;
  createdAtRaw?: string;
  purpose: string;
  requestDate: string;
  requestDateRaw?: string;
  expiresAtRaw?: string;
  requestedAs: string;
  requestStatus: string;
  action?: string;
  id?: string;
  originalData?: any;
}

type RequestStatusFilter = 'ALL' | 'PENDING' | 'REJECTED' | 'WITHDRAWN' | 'GRANTED' | 'EXPIRED' | 'REVOKED';

interface UserDetailEntry {
  label: string;
  value: string;
}

@Component({
  selector: 'app-asset-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
    DateRangePickerComponent,
    ActionDropdownTriggerDirective,
    FilterPanelComponent,
  ],
  templateUrl: './asset-request.component.html',
  styleUrl: './asset-request.component.scss',
})
export class AssetRequestComponent implements OnInit, AfterViewInit {
  @Input() showActionColumn = true;
  @Input() showPurposeColumn = false;
  @Input() showRequestTypeColumn = true;
  /** When true, inserts Asset Owner after Asset Type (platform admin access requests). */
  @Input() showAssetOwnerColumn = false;
  @Input() showRequestDateColumn = true;
  /** When true, shows Requested At (`createdAt` / `created_at` from API) after Organisation (platform admin access requests). */
  @Input() showCreatedAtColumn = false;
  /** When false, hides the Organisation column (provider My Asset Requests). */
  @Input() showOrganisationColumn = true;
  /** When false, the Action column still appears but only View is offered (platform admin). */
  @Input() canApproveReject = true;
  @Input() hideHorizontalScroll = false;
  @Input() showFilters = false;
  @Input() showOrganisationFilter = false;
  /** When true, shows All / Pending / Rejected / Withdrawn / Granted / Expired / Revoked tabs and drives API `requestStatus` + date column label (Requested At vs Expires At vs Expired At). */
  @Input() enableRequestStatusTabs = false;
  /** When true, table sort triggers `reloadData` and `getSortQueryString()` for server-side ordering (platform admin). */
  @Input() serverSideSorting = false;
  /**
   * When true with `serverSideSorting`, first loads omit `sort` on the API and no column shows as sorted
   * until the user clicks a sortable header (platform admin access requests).
   */
  @Input() applyServerSortOnlyAfterUserSort = false;
  /** When true, shows Export and downloads provider access-request report CSV. */
  @Input() showExportButton = false;
  /** Route prefix for the request conversation page; the action is hidden when unset. */
  @Input() conversationRoute: string | null = null;
  private readonly exportLoader = inject(ExportLoaderService);
  private readonly conversationService = inject(ConversationService);
  private readonly workspaceContext = inject(WorkspaceContextService);
  @ViewChild('expandCell', { static: false }) expandCellTemplate!: TemplateRef<any>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('requestDateCell', { static: false }) requestDateCellTemplate!: TemplateRef<any>;
  @ViewChild('purposeCell', { static: false }) purposeCellTemplate!: TemplateRef<any>;
  @ViewChild(FilterPanelComponent) filterPanel?: FilterPanelComponent;
  @Output() reloadData = new EventEmitter<void>();
  selectedRequestStatus: RequestStatusFilter = 'ALL';
  readonly requestStatusTabs: Array<{ label: string; value: RequestStatusFilter }> = [
    { label: 'All', value: 'ALL' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Withdrawn', value: 'WITHDRAWN' },
    { label: 'Granted', value: 'GRANTED' },
    { label: 'Expired', value: 'EXPIRED' },
    { label: 'Revoked', value: 'REVOKED' },
  ];

  openDropdownId: string | null = null;
  private readonly rowExpansion = new SingleRowExpansionState();
  showPurposeModal = false;
  purposeModalText = '';

  // Approve modal state
  isApproveModalVisible = false;
  selectedRequest: AssetRequestData | null = null;
  expirationDate: DateRange | null = null;
  minDate: Date = new Date();

  constructor(
    private formatService: FormatService,
    private aclApdApiService: AclApdApiService,
    private authApiService: AuthApiService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private toasterService: ToasterService,
  ) {}

  columns: TableColumn[] = [
    {
      key: 'expand',
      label: '',
      sortable: false,
      width: '44px',
      fixed: 'left',
    },
    {
      key: 'assetName',
      label: 'Asset Name',
      sortable: false,
      width: '280px',
    },
    {
      key: 'assetType',
      label: 'Asset Type',
      sortable: false,
      width: '130px',
    },
    {
      key: 'requestType',
      label: 'Request Type',
      sortable: false,
      width: '130px',
    },
    {
      key: 'requestedBy',
      label: 'Requested By',
      sortable: false,
      width: '160px',
    },
    {
      key: 'requestDate',
      label: 'Requested At',
      sortable: true,
      align: 'left',
      width: '176px',
    },
    {
      key: 'organisationName',
      label: 'Organisation',
      sortable: false,
      width: '160px',
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '120px',
      align: 'left',
      fixed: 'right',
    },
  ];

  private readonly requestStatusColumn: TableColumn = {
    key: 'requestStatus',
    label: 'Status',
    sortable: true,
    width: '130px',
  };

  /** API `requestStatus` query param; omitted on the All and Expired tabs. */
  getRequestStatusForApi(): string | undefined {
    if (this.selectedRequestStatus === 'ALL' || this.selectedRequestStatus === 'EXPIRED') {
      return undefined;
    }
    return this.selectedRequestStatus;
  }

  private syncColumnsForRequestStatusTab(): void {
    if (!this.enableRequestStatusTabs) {
      return;
    }

    const hasStatusColumn = this.columns.some((column) => column.key === 'requestStatus');
    const shouldShowStatusColumn = this.selectedRequestStatus === 'ALL';

    if (shouldShowStatusColumn && !hasStatusColumn) {
      const requestDateIndex = this.columns.findIndex((column) => column.key === 'requestDate');
      const insertAt = requestDateIndex >= 0 ? requestDateIndex : this.columns.length - 1;
      this.columns = [
        ...this.columns.slice(0, insertAt),
        { ...this.requestStatusColumn },
        ...this.columns.slice(insertAt),
      ];
      this.assignCellTemplates();
    } else if (!shouldShowStatusColumn && hasStatusColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'requestStatus');
    }

    this.updateRequestDateColumnLabel();
  }

  /** True when Accept/Reject should be disabled for the current tab and row. */
  isApproveRejectDisabled(row: AssetRequestData): boolean {
    if (this.enableRequestStatusTabs) {
      if (this.selectedRequestStatus === 'PENDING') {
        return false;
      }
      if (this.selectedRequestStatus === 'ALL') {
        const status = (row?.requestStatus ?? '').toString().toLowerCase();
        return status !== 'pending';
      }
      return true;
    }

    const s = (row?.requestStatus ?? '').toString().toLowerCase();
    return s === 'granted' || s === 'approved' || s === 'rejected';
  }

  /** Whether to show an action in the dropdown based on the active status tab. */
  showAssetRequestAction(action: 'view' | 'accept' | 'reject'): boolean {
    if (action === 'view') {
      return true;
    }
    if (!this.canApproveReject) {
      return false;
    }
    if (this.enableRequestStatusTabs) {
      return this.selectedRequestStatus === 'PENDING' || this.selectedRequestStatus === 'ALL';
    }
    return action === 'accept' || action === 'reject';
  }

  /** Returns chip variant class for Request Status (pill styling). */
  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip--default';
    const s = status.toLowerCase();
    if (s === 'granted' || s === 'approved') return 'status-chip status-chip--granted';
    if (s === 'pending') return 'status-chip status-chip--pending';
    if (s === 'rejected') return 'status-chip status-chip--rejected';
    if (s === 'revoked') return 'status-chip status-chip--rejected';
    return 'status-chip status-chip--default';
  }

  // Original data (unfiltered) - will be populated from API
  allAssetRequestsData: AssetRequestData[] = [];

  // Asset requests data for current page - will be populated from API
  assetRequestsData: AssetRequestData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  /** Active sort for `access_request/platform` when `serverSideSorting` is true (UI column key → API field via `getApiSortField`). */
  currentSort: { field: string; order: 'asc' | 'desc' } = { field: 'requestDate', order: 'desc' };
  /** When `applyServerSortOnlyAfterUserSort` is set, becomes true after the user picks a column sort. */
  private userChoseServerSort = false;

  loading = false;
  emptyMessage = 'No asset requests found';
  filterFields: FilterField[] = [
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'dateRange',
      placeholder: 'Choose Date Range',
      value: null,
    },
  ];

  private activeFilters: Record<string, any> = {};
  private lastDateRangeQueryKey = '';
  /** Tracks the org ID that was last used to call the API, so any change (select or clear) triggers a reload. */
  private lastEmittedOrgId: string | null = null;
  /** Guards against duplicate reloads while clearing filters on status tab change. */
  private isResettingTabFilters = false;

  get hasActiveDateFilter(): boolean {
    const dateRange = this.activeFilters['dateRange'] as DateRange | null;
    return !!(dateRange?.startDate && dateRange?.endDate);
  }

  get hasActiveOrgFilter(): boolean {
    return !!this.getOrganisationIdForApi();
  }

  get hasActiveLocalFilter(): boolean {
    return this.hasActiveDateFilter;
  }

  get filteredAssetRequestsData(): AssetRequestData[] {
    // Server-side pagination: API already applies date/org filters; do not slice the current page locally.
    if (this.pagination.serverSide) {
      return this.assetRequestsData;
    }

    let rows = this.assetRequestsData;

    // Date range — local filter (org is handled server-side)
    const dateRange = this.activeFilters['dateRange'] as DateRange | null;
    if (dateRange?.startDate && dateRange?.endDate) {
      const start = new Date(dateRange.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999);
      rows = rows.filter((row) => {
        const candidate = new Date(row.requestDateRaw || row.requestDate || '');
        if (isNaN(candidate.getTime())) return false;
        return candidate >= start && candidate <= end;
      });
    }

    return rows;
  }

  get effectivePagination(): PaginationConfig {
    // Keep API totalItems/page for server-side lists (platform/provider access requests).
    if (this.pagination.serverSide) {
      return this.pagination;
    }

    if (!this.hasActiveLocalFilter) {
      return this.pagination;
    }

    return {
      ...this.pagination,
      currentPage: 1,
      totalItems: this.filteredAssetRequestsData.length,
    };
  }

  /** Returns the selected organisation ID to pass as a query param to the API. */
  getOrganisationIdForApi(): string | undefined {
    const items: any[] = this.getOrganisationSelections(this.activeFilters);
    if (items.length === 0) return undefined;
    const first = items[0];
    // multiselect emits full option objects {label, value} — extract the value (org ID)
    return typeof first === 'object' ? first?.value : first;
  }

  private getOrganisationIdFromFilters(filters: Record<string, any>): string | undefined {
    const items: any[] = this.getOrganisationSelections(filters);
    if (items.length === 0) return undefined;
    const first = items[0];
    return typeof first === 'object' ? first?.value : first;
  }

  private getOrganisationSelections(filters: Record<string, any>): any[] {
    const org = filters['organisation'];
    if (Array.isArray(org)) {
      return org;
    }
    if (org === null || org === undefined || org === '') {
      return [];
    }
    return [org];
  }

  onFilterChange(filters: Record<string, any>): void {
    if (this.isResettingTabFilters) {
      return;
    }

    const previousDateKey = this.getDateRangeQueryKey();

    this.activeFilters = { ...filters };

    const nextDateKey = this.getDateRangeQueryKey();
    const nextOrgId = this.getOrganisationIdForApi() ?? null;

    const dateChanged = previousDateKey !== nextDateKey;
    const orgChanged = nextOrgId !== this.lastEmittedOrgId;

    if (dateChanged || orgChanged) {
      this.lastEmittedOrgId = nextOrgId;
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.lastDateRangeQueryKey = nextDateKey;
      this.reloadData.emit();
    }
  }

  onClearFilters(): void {
    const hadDateFilter = this.hasActiveDateFilter;
    const hadOrgFilter = this.hasActiveOrgFilter;
    this.activeFilters = {};
    this.lastDateRangeQueryKey = '';
    this.lastEmittedOrgId = null;
    if (hadDateFilter || hadOrgFilter) {
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.reloadData.emit();
    }
  }

  getDateRangeForApi(): { startDate: string; endDate: string } | undefined {
    return this.formatService.formatDateRangeForApi(this.activeFilters['dateRange']);
  }

  private getDateRangeQueryKey(): string {
    const range = this.getDateRangeForApi();
    if (!range) return '';
    return `${range.startDate}|${range.endDate}`;
  }

  private refreshFilterOptions(): void {
    // Kept for future extensibility when additional filters are re-enabled.
  }

  private async loadOrganisationFilter(): Promise<void> {
    try {
      const response = await this.authApiService.getAllOrganisations(1, 500);
      let orgs: any[] = [];
      if (response?.result && Array.isArray(response.result)) {
        orgs = response.result;
      } else if (Array.isArray(response)) {
        orgs = response;
      }

      // Use org id as value so we can pass it directly as a query param
      const seen = new Set<string>();
      const options = orgs
        .filter((org: any) => org.id && org.orgName)
        .filter((org: any) => {
          if (seen.has(org.id)) return false;
          seen.add(org.id);
          return true;
        })
        .sort((a: any, b: any) => a.orgName.localeCompare(b.orgName))
        .map((org: any) => ({ label: org.orgName as string, value: org.id as string }));

      // Insert organisation filter before the date range filter
      this.filterFields = [
        {
          key: 'organisation',
          label: 'Organisation',
          type: 'multiselect',
          placeholder: 'Select Organisation',
          options,
          value: [],
          maxSelected: 1,
          showClear: true,
        },
        ...this.filterFields,
      ];
    } catch (err) {
      console.error('Failed to load organisations for filter:', err);
    }
  }

  ngOnInit(): void {
    if (!this.showActionColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'action');
    }
    if (!this.showPurposeColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'purpose');
    }
    if (!this.showRequestTypeColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'requestType');
    }
    if (this.showAssetOwnerColumn) {
      const assetTypeIdx = this.columns.findIndex((column) => column.key === 'assetType');
      const insertAt = assetTypeIdx >= 0 ? assetTypeIdx + 1 : 1;
      this.columns = [
        ...this.columns.slice(0, insertAt),
        { key: 'assetOwner', label: 'Asset Owner', sortable: false, width: '160px' },
        ...this.columns.slice(insertAt),
      ];
    }
    if (!this.showRequestDateColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'requestDate');
    }
    if (!this.showCreatedAtColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'createdAt');
    }
    if (!this.showOrganisationColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'organisationName');
    }
    if (this.enableRequestStatusTabs) {
      this.applyRequestStatusFromUrl();
      this.syncColumnsForRequestStatusTab();
    }

    if (this.showOrganisationFilter) {
      this.loadOrganisationFilter();
    }
    // Data will be loaded from parent component via mapProviderAssetRequestsResponse()
    // Don't call loadAssetRequests() here
  }

  ngAfterViewInit(): void {
    // Assign cell templates synchronously to avoid race condition with data loading
    this.assignCellTemplates();
    this.cdr.detectChanges();
  }

  private assignCellTemplates(): void {
    const expandColumn = this.columns.find(col => col.key === 'expand');
    if (expandColumn && this.expandCellTemplate) {
      expandColumn.cellTemplate = this.expandCellTemplate;
    }
    const purposeColumn = this.columns.find(col => col.key === 'purpose');
    if (purposeColumn && this.purposeCellTemplate) {
      purposeColumn.cellTemplate = this.purposeCellTemplate;
    }
    const statusColumn = this.columns.find(col => col.key === 'requestStatus');
    if (statusColumn && this.statusCellTemplate) {
      statusColumn.cellTemplate = this.statusCellTemplate;
    }
    const requestDateColumn = this.columns.find(col => col.key === 'requestDate');
    if (requestDateColumn && this.requestDateCellTemplate) {
      requestDateColumn.cellTemplate = this.requestDateCellTemplate;
    }
    const actionColumn = this.columns.find(col => col.key === 'action');
    if (actionColumn && this.actionCellTemplate) {
      actionColumn.cellTemplate = this.actionCellTemplate;
    }
  }

  openPurposeModal(purpose: string): void {
    this.purposeModalText = displayValueOrDash(purpose);
    this.showPurposeModal = true;
    this.cdr.detectChanges();
  }

  getUserDetailEntriesForRow(row: AssetRequestData): UserDetailEntry[] {
    const userObject = row.originalData?.user;
    if (!userObject || typeof userObject !== 'object') {
      return [{ label: 'Details', value: 'User details are not available for this request.' }];
    }
    return this.buildUserDetailEntries(userObject);
  }

  getStatusActorEntry(row: AssetRequestData): UserDetailEntry | null {
    const status = (row?.requestStatus ?? '').toString().toLowerCase();
    if (status === 'granted' || status === 'approved') {
      return { label: 'Approved by', value: this.resolveStatusActorName(row.originalData, 'approved') };
    }
    if (status === 'rejected') {
      return { label: 'Rejected by', value: this.resolveRejectedByDisplayName(row.originalData) };
    }
    if (status === 'revoked') {
      return { label: 'Revoked by', value: this.resolveStatusActorName(row.originalData, 'revoked') };
    }
    return null;
  }

  /** Name of the person who rejected the request. */
  private resolveRejectedByDisplayName(item: any): string {
    const sources = [item, item?.asset, item?.additionalInfo].filter(
      (src) => src && typeof src === 'object',
    );
    for (const source of sources) {
      const candidates = [source.rejectedBy, source.rejected_by, source];
      for (const candidate of candidates) {
        if (!candidate || typeof candidate !== 'object') continue;
        const person = candidate as Record<string, unknown>;
        const named = this.formatActorValue({
          rejectedByFirstName:
            person['rejectedByFirstName'] ?? person['rejected_by_first_name'],
          rejectedByLastName:
            person['rejectedByLastName'] ?? person['rejected_by_last_name'],
        });
        if (named !== '-') return named;
      }
    }
    return this.resolveStatusActorName(item, 'rejected');
  }

  private resolveStatusActorName(
    item: any,
    kind: 'approved' | 'rejected' | 'revoked',
  ): string {
    if (!item || typeof item !== 'object') return '-';
    const keySets: Record<'approved' | 'rejected' | 'revoked', string[]> = {
      approved: [
        'approvedBy',
        'approved_by',
        'grantedBy',
        'granted_by',
        'approvedByName',
        'approved_by_name',
        'approvedByUsername',
        'approved_by_username',
      ],
      rejected: [
        'rejectedBy',
        'rejected_by',
        'rejectedByName',
        'rejected_by_name',
        'rejectedByUsername',
        'rejected_by_username',
      ],
      revoked: [
        'revokedBy',
        'revoked_by',
        'revokedByName',
        'revoked_by_name',
        'revokedByUsername',
        'revoked_by_username',
      ],
    };
    const sources = [item, item.asset, item.additionalInfo].filter(
      (src) => src && typeof src === 'object',
    );
    for (const source of sources) {
      for (const key of keySets[kind]) {
        const name = this.formatActorValue(source[key]);
        if (name !== '-') return name;
      }
    }
    if (kind === 'approved') {
      const ownerName = this.formatActorValue(item.owner ?? item.asset?.owner);
      if (ownerName !== '-') return ownerName;
    }
    return '-';
  }

  private formatActorValue(raw: unknown): string {
    if (raw == null || raw === '') return '-';
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed || '-';
    }
    if (typeof raw !== 'object') return '-';
    const person = raw as Record<string, unknown>;
    const first = String(
      person['firstName'] ??
        person['first_name'] ??
        person['rejectedByFirstName'] ??
        person['rejected_by_first_name'] ??
        person['ownerFirstName'] ??
        person['owner_first_name'] ??
        '',
    ).trim();
    const last = String(
      person['lastName'] ??
        person['last_name'] ??
        person['rejectedByLastName'] ??
        person['rejected_by_last_name'] ??
        person['ownerLastName'] ??
        person['owner_last_name'] ??
        '',
    ).trim();
    const combined = [first, last].filter(Boolean).join(' ').trim();
    if (first && last && first.toLowerCase() === last.toLowerCase()) {
      return first;
    }
    if (combined) return combined;
    const fallback = String(
      person['name'] ??
        person['displayName'] ??
        person['display_name'] ??
        person['username'] ??
        person['userName'] ??
        person['email'] ??
        '',
    ).trim();
    return fallback || '-';
  }

  private buildUserDetailEntries(userObject: Record<string, any>): UserDetailEntry[] {
    const firstName = (
      userObject['consumerFirstName'] ||
      userObject['consumer_first_name'] ||
      userObject['firstName'] ||
      userObject['first_name'] ||
      ''
    ).toString().trim();

    const lastName = (
      userObject['consumerLastName'] ||
      userObject['consumer_last_name'] ||
      userObject['lastName'] ||
      userObject['last_name'] ||
      ''
    ).toString().trim();

    const combinedName = this.formatActorValue({ firstName, lastName });

    const details = Object.entries(userObject)
      .filter(([key, value]) => {
        const normalizedKey = key.toLowerCase().replace(/_/g, '');
        if (
          normalizedKey === 'consumerid' ||
          normalizedKey === 'consumerfirstname' ||
          normalizedKey === 'consumerlastname' ||
          normalizedKey === 'firstname' ||
          normalizedKey === 'lastname'
        ) {
          return false;
        }
        return value !== undefined && value !== null && String(value).trim() !== '';
      })
      .map(([key, value]) => ({
        label: this.formatUserDetailLabel(key),
        value: String(value),
      }));

    if (combinedName !== '-') {
      details.unshift({ label: 'Name', value: combinedName });
    }

    return details;
  }

  private formatUserDetailLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .replace(/^Consumer\s+/i, '');
  }

  /** Prefer `asset.organization` from the access-request payload; fall back to other known shapes. */
  private resolveOrganisationName(item: any): string {
    const candidates = [
      item?.asset?.organization,
      item?.asset?.organisation,
      item?.asset?.organisationName,
      item?.asset?.organisation_name,
      item?.organisationName,
      item?.organisation_name,
      item?.organization,
      item?.organisation,
      item?.orgName,
      item?.org_name,
      item?.additionalInfo?.organisationName,
      item?.additionalInfo?.organisation_name,
      item?.additionalInfo?.organization,
      item?.additionalInfo?.organisation,
      item?.user?.consumerOrganization,
      item?.user?.consumer_organization,
      item?.user?.organization,
      item?.user?.organisation,
    ];

    for (const candidate of candidates) {
      const value = this.coerceOrganisationDisplay(candidate);
      if (value) {
        return value;
      }
    }
    return '-';
  }

  private coerceOrganisationDisplay(raw: unknown): string {
    if (raw == null) {
      return '';
    }
    if (typeof raw === 'string' || typeof raw === 'number') {
      return String(raw).trim();
    }
    if (typeof raw === 'object') {
      const org = raw as Record<string, unknown>;
      const name =
        org['name'] ??
        org['organisationName'] ??
        org['organisation_name'] ??
        org['organizationName'] ??
        org['orgName'];
      return name == null ? '' : String(name).trim();
    }
    return '';
  }

  private getRequestedByDisplay(item: any): string {
    const firstName = (
      item.user?.consumerFirstName ||
      item.user?.consumer_first_name ||
      item.user?.firstName ||
      item.user?.first_name ||
      ''
    ).toString().trim();

    const lastName = (
      item.user?.consumerLastName ||
      item.user?.consumer_last_name ||
      item.user?.lastName ||
      item.user?.last_name ||
      ''
    ).toString().trim();

    const fullName = this.formatActorValue({ firstName, lastName });

    return displayValueOrDash(
      (fullName !== '-' ? fullName : '') ||
        item.user?.consumerEmail ||
        item.userName ||
        item.user_name ||
        item.requestedBy ||
        item.requested_by,
    );
  }

  private getAssetOwnerDisplay(item: any): string {
    const firstName = (
      item.owner?.ownerFirstName ||
      item.owner?.owner_first_name ||
      item.owner?.firstName ||
      item.owner?.first_name ||
      ''
    ).toString().trim();

    const lastName = (
      item.owner?.ownerLastName ||
      item.owner?.owner_last_name ||
      item.owner?.lastName ||
      item.owner?.last_name ||
      ''
    ).toString().trim();

    const fullName = this.formatActorValue({ firstName, lastName });

    return displayValueOrDash(
      (fullName !== '-' ? fullName : '') ||
        item.owner?.ownerEmail ||
        item.owner?.owner_email ||
        '',
    );
  }

  /** Unwrap list + paginationInfo from platform/provider access-request APIs. */
  private normalizeListResponse(response: any): {
    result: any[];
    paginationInfo?: Record<string, unknown>;
  } {
    if (!response || typeof response !== 'object') {
      return { result: [] };
    }

    const rootPagination = response['paginationInfo'] as Record<string, unknown> | undefined;
    const rootResult = response['result'];

    if (rootPagination || rootResult !== undefined) {
      return {
        result: Array.isArray(rootResult) ? rootResult : [],
        paginationInfo: rootPagination,
      };
    }

    const nested = response['data'];
    if (nested && typeof nested === 'object') {
      const nestedPagination = nested['paginationInfo'] as Record<string, unknown> | undefined;
      const nestedResult = nested['result'];
      if (nestedPagination || nestedResult !== undefined) {
        return {
          result: Array.isArray(nestedResult) ? nestedResult : [],
          paginationInfo: nestedPagination ?? rootPagination,
        };
      }
    }

    if (Array.isArray(response)) {
      return { result: response };
    }

    return { result: [], paginationInfo: rootPagination };
  }

  private getMaxPage(totalItems: number, pageSize: number): number {
    return Math.max(1, Math.ceil(totalItems / pageSize));
  }

  /** Sync table pagination from API `paginationInfo` (source of truth for page controls). */
  private applyPaginationFromApi(paginationInfo?: Record<string, unknown>, fallbackCount = 0): void {
    if (!paginationInfo) {
      this.pagination = {
        ...this.pagination,
        totalItems: fallbackCount,
        currentPage: 1,
      };
      return;
    }

    const totalCount = Number(paginationInfo['totalCount']);
    const apiPage = Number(paginationInfo['page']);
    const apiSize = Number(paginationInfo['size']);

    this.pagination = {
      ...this.pagination,
      totalItems: Number.isFinite(totalCount) ? totalCount : fallbackCount,
      currentPage: Number.isFinite(apiPage) && apiPage > 0 ? apiPage : 1,
      ...(Number.isFinite(apiSize) && apiSize > 0 ? { pageSize: apiSize } : {}),
    };
  }

  /**
   * Map provider asset requests API response to AssetRequestData format
   * @param response API response from providerAssetRequests()
   */
  mapProviderAssetRequestsResponse(response: any): void {
    // Keep existing rows visible during server-side sort/pagination so column widths stay stable.
    const showLoading = this.assetRequestsData.length === 0;
    if (showLoading) {
      this.loading = true;
    }
    let retryOnPreviousPage = false;
    try {
      let mappedData: AssetRequestData[] = [];
      const { result: dataArray, paginationInfo } = this.normalizeListResponse(response);

      if (dataArray.length > 0) {
        mappedData = dataArray.map((item: any) => {
          // Format request date
          const createdAtRaw = item.createdAt ?? item.created_at ?? '';
          const expiresAtRaw = item.expiryAt ?? item.expiry_at ?? item.expiresAt ?? item.expires_at ?? '';
          const effectiveDateRaw = this.resolveEffectiveDateRaw(createdAtRaw, expiresAtRaw);

          // Requested As (role/entity request is being made as)
          const requestedAs = this.formatService.toPascalCase(
            item.requestAs ||
            item.request_as ||
            item.requestedAs ||
            item.requested_as ||
            item.additionalInfo?.requestAs ||
            item.additionalInfo?.request_as ||
            item.additionalInfo?.requestedAs ||
            item.additionalInfo?.requested_as ||
            item.user?.requestedAs ||
            item.user?.requested_as ||
            item.role ||
            item.accessType ||
            ''
          );
          
          // Format request type to PascalCase
          const requestType = this.formatService.toPascalCase(item.requestType || item.accessType || '');
          
          // Format asset type
          let assetType = '-';
          if (item.asset?.assetType || item.assetType) {
            assetType = displayValueOrDash(this.formatService.formatAssetType(item.asset?.assetType || item.assetType));
          }
          
          // Format request status to PascalCase
          const requestStatus = displayValueOrDash(
            this.formatService.toPascalCase(
              item.requestStatus || item.request_status || item.status || '',
            ),
          );

          return {
            assetName: displayValueOrDash(item.asset?.assetName || item.assetName || item.name),
            assetType: assetType,
            requestedBy: this.getRequestedByDisplay(item),
            requestType: displayValueOrDash(requestType),
            assetOwner: this.getAssetOwnerDisplay(item),
            organisationName: this.resolveOrganisationName(item),
            createdAt: createdAtRaw ? this.formatDate(createdAtRaw) : '-',
            createdAtRaw: createdAtRaw || '',
            expiresAtRaw: expiresAtRaw || '',
            purpose: displayValueOrDash(
              item.additionalInfo?.purposeOfRequest || item.additionalInfo?.purpose_of_request || item.purpose,
            ),
            requestDate: effectiveDateRaw ? this.formatDate(effectiveDateRaw) : '-',
            requestDateRaw: effectiveDateRaw || '',
            requestedAs: displayValueOrDash(requestedAs),
            requestStatus: requestStatus,
            action: 'Action',
            originalData: item // Store original API response data
          };
        });
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => {
          const createdAtRaw = item.createdAt ?? item.created_at ?? '';
          const expiresAtRaw = item.expiryAt ?? item.expiry_at ?? item.expiresAt ?? item.expires_at ?? '';
          const effectiveDateRaw = this.resolveEffectiveDateRaw(createdAtRaw, expiresAtRaw);
          const requestedAs = this.formatService.toPascalCase(
            item.requestAs ||
            item.request_as ||
            item.requestedAs ||
            item.requested_as ||
            item.additionalInfo?.requestAs ||
            item.additionalInfo?.request_as ||
            item.additionalInfo?.requestedAs ||
            item.additionalInfo?.requested_as ||
            item.user?.requestedAs ||
            item.user?.requested_as ||
            item.role ||
            item.accessType ||
            ''
          );
          const requestType = this.formatService.toPascalCase(item.requestType || item.accessType || '');
          let assetType = '-';
          if (item.asset?.assetType || item.assetType) {
            assetType = displayValueOrDash(this.formatService.formatAssetType(item.asset?.assetType || item.assetType));
          }
          const requestStatus = displayValueOrDash(
            this.formatService.toPascalCase(item.requestStatus || item.request_status || item.status || ''),
          );

          return {
            assetName: displayValueOrDash(item.asset?.assetName || item.assetName || item.name),
            assetType: assetType,
            requestedBy: this.getRequestedByDisplay(item),
            requestType: displayValueOrDash(requestType),
            assetOwner: this.getAssetOwnerDisplay(item),
            organisationName: this.resolveOrganisationName(item),
            createdAt: createdAtRaw ? this.formatDate(createdAtRaw) : '-',
            createdAtRaw: createdAtRaw || '',
            expiresAtRaw: expiresAtRaw || '',
            purpose: displayValueOrDash(
              item.additionalInfo?.purposeOfRequest || item.additionalInfo?.purpose_of_request || item.purpose,
            ),
            requestDate: effectiveDateRaw ? this.formatDate(effectiveDateRaw) : '-',
            requestDateRaw: effectiveDateRaw || '',
            requestedAs: displayValueOrDash(requestedAs),
            requestStatus: requestStatus,
            action: 'Action',
            originalData: item
          };
        });
      }

      // Always derive paginator state from API paginationInfo when present (including totalCount: 0).
      this.applyPaginationFromApi(paginationInfo, mappedData.length);

      const maxPage = this.getMaxPage(this.pagination.totalItems, this.pagination.pageSize);
      if (this.pagination.currentPage > maxPage) {
        this.pagination = { ...this.pagination, currentPage: maxPage };
        retryOnPreviousPage = true;
      }

      if (retryOnPreviousPage) {
        this.reloadData.emit();
        return;
      }

      this.assetRequestsData = mappedData;
      this.allAssetRequestsData = [...mappedData];
      this.refreshFilterOptions();
      // Ensure templates are assigned and trigger change detection
      this.assignCellTemplates();
      this.cdr.detectChanges();
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No asset requests found';
      }
    } catch (error) {
      console.error('Error mapping provider asset requests response:', error);
      this.assetRequestsData = [];
      this.allAssetRequestsData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No asset requests found';
    } finally {
      if (!retryOnPreviousPage) {
        this.loading = false;
      }
    }
  }

  /** Format as DD-MMM-YYYY HH:mm in IST for Requested At column */
  formatDate(dateString: string): string {
    return this.formatService.formatDate(dateString) || '';
  }

  private extractDateString(input: unknown): string {
    if (!input) return '';
    if (typeof input === 'string' || typeof input === 'number') return String(input);
    if (typeof input === 'object') {
      const obj = input as Record<string, unknown>;
      const nested =
        obj['value'] ??
        obj['@value'] ??
        obj['date'] ??
        obj['dateTime'] ??
        obj['datetime'] ??
        obj['timestamp'];
      if (nested != null) return String(nested);
    }
    return '';
  }

  getDateTimeParts(
    dateValue: unknown,
    assumeIstWhenNoTimezone = false,
  ): { date: string; time: string } {
    const raw = this.extractDateString(dateValue);
    if (!raw) return { date: '-', time: '' };
    const formatted = this.formatService.formatDate(raw, assumeIstWhenNoTimezone);
    if (!formatted) return { date: raw, time: '' };
    const spaceIdx = formatted.indexOf(' ');
    if (spaceIdx === -1) return { date: formatted, time: '' };
    return { date: formatted.slice(0, spaceIdx), time: formatted.slice(spaceIdx + 1) };
  }

  getRequestDateCellValue(row: AssetRequestData): { date: string; time: string } {
    const showExpiresAt = this.enableRequestStatusTabs && (
      this.selectedRequestStatus === 'GRANTED' ||
      this.selectedRequestStatus === 'EXPIRED'
    );
    const raw = showExpiresAt
      ? (row.expiresAtRaw || '')
      : (row.createdAtRaw || row.requestDateRaw || row.requestDate || '');
    return this.getDateTimeParts(raw);
  }

  private resolveEffectiveDateRaw(createdAtRaw: string, expiresAtRaw: string): string {
    if (this.selectedRequestStatus === 'GRANTED' || this.selectedRequestStatus === 'EXPIRED') {
      return expiresAtRaw;
    }
    return createdAtRaw;
  }

  private updateRequestDateColumnLabel(): void {
    const requestDateColumn = this.columns.find((col) => col.key === 'requestDate');
    if (requestDateColumn) {
      requestDateColumn.label =
        this.selectedRequestStatus === 'EXPIRED'
          ? 'Expired At'
          : this.selectedRequestStatus === 'GRANTED'
            ? 'Expires At'
            : 'Requested At';
    }
  }

  setRequestStatusTab(status: RequestStatusFilter): void {
    if (this.selectedRequestStatus === status) return;

    this.isResettingTabFilters = true;
    try {
      if (this.enableRequestStatusTabs) {
        this.resetFiltersOnTabChange();
      }

      this.selectedRequestStatus = status;
      this.emptyMessage =
        status === 'REVOKED' ? 'No revoked asset requests found' : 'No asset requests found';
      this.syncColumnsForRequestStatusTab();
      this.collapseExpandedRows();
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.assetRequestsData = [];
      this.allAssetRequestsData = [];
      this.syncRequestStatusQueryParam();
      this.reloadData.emit();
    } finally {
      this.isResettingTabFilters = false;
    }
  }

  /** Restore Pending / Rejected / Granted (and the other status tabs) after returning from Conversation. */
  private applyRequestStatusFromUrl(): void {
    const status = this.parseRequestStatus(
      this.route.snapshot.queryParamMap.get('requestStatus'),
    );
    if (!status) return;
    this.selectedRequestStatus = status;
    this.emptyMessage =
      status === 'REVOKED'
        ? 'No revoked asset requests found'
        : 'No asset requests found';
  }

  private parseRequestStatus(value: string | null): RequestStatusFilter | null {
    if (!value) return null;
    const status = value.toUpperCase() as RequestStatusFilter;
    return this.requestStatusTabs.some((tab) => tab.value === status) ? status : null;
  }

  /** Keep the active status tab in the URL so Conversation's back link can return to it. */
  private syncRequestStatusQueryParam(): void {
    if (!this.enableRequestStatusTabs) return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        requestStatus:
          this.selectedRequestStatus === 'ALL' ? null : this.selectedRequestStatus,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Clear filters and collapse the panel when switching Pending / Rejected / Withdrawn / Granted. */
  private resetFiltersOnTabChange(): void {
    this.activeFilters = {};
    this.lastDateRangeQueryKey = '';
    this.lastEmittedOrgId = null;

    this.filterFields = this.filterFields.map((field) => {
      if (field.type === 'multiselect') {
        return { ...field, value: [] };
      }
      if (field.type === 'dateRange') {
        return { ...field, value: null };
      }
      return { ...field, value: '' };
    });

    this.cdr.detectChanges();
    this.filterPanel?.resetPanel({ collapse: true });
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    // Emit event to parent to reload data with new page
    this.reloadData.emit();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    // Emit event to parent to reload data with new page size
    this.reloadData.emit();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    if (this.serverSideSorting) {
      this.userChoseServerSort = true;
      this.currentSort = { field: event.column, order: event.direction };
      this.pagination = { ...this.pagination, currentPage: 1 };
      this.reloadData.emit();
      return;
    }
    // Client-side sort of the current page slice only
    this.assetRequestsData.sort((a, b) => {
      const aValue = a[event.column as keyof AssetRequestData] || '';
      const bValue = b[event.column as keyof AssetRequestData] || '';

      if (event.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  /** `sort` query value for `access_request/platform` (e.g. `createdAt:desc`). */
  getSortQueryString(): string | undefined {
    if (!this.serverSideSorting) {
      return undefined;
    }
    if (this.applyServerSortOnlyAfterUserSort && !this.userChoseServerSort) {
      return undefined;
    }
    return `${this.getApiSortField(this.currentSort.field)}:${this.currentSort.order}`;
  }

  /** Whether the table should show an active sort indicator for server-side mode. */
  get serverSortUiActive(): boolean {
    return this.serverSideSorting && (!this.applyServerSortOnlyAfterUserSort || this.userChoseServerSort);
  }

  private getApiSortField(field: string): string {
    // Single date column: Granted tab → `expiryAt`; otherwise → `createdAt` (Requested At).
    if (field === 'requestDate') {
      if (
        this.enableRequestStatusTabs &&
        (this.selectedRequestStatus === 'GRANTED' || this.selectedRequestStatus === 'EXPIRED')
      ) {
        return 'expiryAt';
      }
      return 'createdAt';
    }

    const fieldMapping: Record<string, string> = {
      assetName: 'assetName',
      requestType: 'requestType',
      createdAt: 'createdAt',
    };
    return fieldMapping[field] ?? field;
  }

  private readonly inlineActionDropdown = new InlineActionDropdownMenuController();

  /** Stable id for action menu state; must match `[attr.data-dropdown-id]` in the template. */
  getActionDropdownRowId(row: AssetRequestData): string {
    const data = row.originalData;
    const id = data?.requestId ?? data?.id ?? row.id;
    return id != null && id !== '' ? String(id) : '';
  }

  toggleActionDropdown(event: Event, row: AssetRequestData): void {
    event.stopPropagation();
    const rowId = this.getActionDropdownRowId(row);
    if (this.openDropdownId === rowId) {
      this.closeActionDropdown();
      return;
    }
    this.openDropdownId = rowId;
    this.cdr.detectChanges();
    openInlineTableActionDropdown({
      controller: this.inlineActionDropdown,
      event,
      rowId,
      onAnchorDetached: () => this.closeActionDropdown(),
    });
  }

  /** Collapse all child rows (e.g. when switching status tabs). */
  collapseExpandedRows(): void {
    this.rowExpansion.collapseAll();
    this.closeActionDropdown();
  }

  toggleRowExpansion(row: AssetRequestData): void {
    const rowId = this.getActionDropdownRowId(row) || JSON.stringify(row);
    this.rowExpansion.toggle(rowId);
  }

  isRowExpanded(row: AssetRequestData): boolean {
    const rowId = this.getActionDropdownRowId(row) || JSON.stringify(row);
    return this.rowExpansion.isExpanded(rowId);
  }

  getIsRowExpanded(): (row: AssetRequestData) => boolean {
    return (row: AssetRequestData) => this.isRowExpanded(row);
  }

  closeActionDropdown(): void {
    this.inlineActionDropdown.close();
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequestData): boolean {
    const rowId = this.getActionDropdownRowId(row);
    const isOpen = this.openDropdownId === rowId;
    return isOpen;
  }

  canConverse(row: AssetRequestData): boolean {
    if (this.workspaceContext.isDelegateSession()) return false;
    const requestId = this.getConversationRequestId(row);
    const status = this.effectiveConversationStatus(row);
    if (
      !this.conversationRoute ||
      !requestId ||
      !canOpenConversation(status, row?.expiresAtRaw) ||
      !this.conversationService.isAssetOwner(row.originalData?.owner?.ownerId)
    ) {
      return false;
    }
    return true;
  }

  /** A granted request that has passed its expiry is treated as expired. */
  private effectiveConversationStatus(row: AssetRequestData): string {
    const status = (row?.requestStatus ?? '').trim().toLowerCase();
    if (status === 'revoked' || status === 'expired') {
      return status;
    }
    const grantExpired =
      (status === 'granted' || status === 'approved' || status === 'active') &&
      !isAccessStillActive(row?.expiresAtRaw);
    if (grantExpired || (this.enableRequestStatusTabs && this.selectedRequestStatus === 'EXPIRED')) {
      return 'expired';
    }
    return status;
  }

  private getConversationRequestId(row: AssetRequestData): string {
    return row.originalData?.id || row.originalData?.requestId || row.id || '';
  }

  private openConversation(row: AssetRequestData): void {
    const queryParams: Record<string, string> = { tab: 'assetRequest' };
    if (this.enableRequestStatusTabs && this.selectedRequestStatus !== 'ALL') {
      queryParams['requestStatus'] = this.selectedRequestStatus;
    }
    void this.router.navigate([this.conversationRoute, this.getConversationRequestId(row), 'conversation'], {
      queryParams,
      state: {
        assetName: row.assetName,
        purpose: row.purpose,
        status: this.effectiveConversationStatus(row),
      },
    });
  }

  async handleAction(action: string, row: AssetRequestData): Promise<void> {
    this.closeActionDropdown();

    if (action === 'view') {
      this.viewAssetRequest(row);
      return;
    }

    if (action === 'conversation') {
      this.openConversation(row);
      return;
    }

    if (action === 'accept') {
      // Open approve modal to capture expiry before approving
      this.selectedRequest = row;
      this.expirationDate = null;
      // Defer modal open to next tick so dropdown close doesn't interfere with click
      setTimeout(() => {
        this.isApproveModalVisible = true;
        this.cdr.detectChanges();
      }, 0);
      return;
    }

    const requestId = row.originalData?.id || row.originalData?.requestId;
    if (!requestId) {
      console.error('Request ID not found');
      return;
    }

    try {
      let payload: any = {};
      
      if (action === 'reject') {
        payload = { 
          requestId: requestId,
          status: 'rejected' 
        };
      } else {
        console.warn('Unknown action:', action);
        return;
      }

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      this.toasterService.success('Asset request has been rejected successfully');

      // Reload data to reflect the updated status
      this.reloadData.emit();
    } catch (error) {
      console.error(`Error ${action}ing access request:`, error);
      // Error toaster is handled by networkService interceptor
    }
  }

  /** Confirm handler for approve modal - sends status=granted with expiryAt */
  async onApproveConfirm(): Promise<void> {
    if (!this.selectedRequest) {
      return;
    }

    const requestId =
      this.selectedRequest.originalData?.id ||
      this.selectedRequest.originalData?.requestId;

    if (!requestId) {
      console.error('Request ID not found while approving');
      return;
    }

    if (!this.expirationDate?.startDate) {
      console.error('Expiry date is required');
      return;
    }

    const expiryAt = this.formatExpiryAt(this.expirationDate.startDate);

    try {
      const payload = {
        requestId: requestId,
        status: 'granted',
        expiryAt: expiryAt,
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      this.toasterService.success('Asset request has been approved successfully');

      this.closeApproveModal();
      this.reloadData.emit();
    } catch (error) {
      console.error('Error approving access request with expiryAt:', error);
      // Error toaster is handled by networkService interceptor
    }
  }

  closeApproveModal(): void {
    this.isApproveModalVisible = false;
    this.selectedRequest = null;
    this.expirationDate = null;
  }

  private viewAssetRequest(row: AssetRequestData): void {
    const original = row.originalData || {};
    const asset = original.asset || {};
    const itemId =
      original.itemId ||
      original.item_id ||
      asset.itemId ||
      asset.item_id;

    if (!itemId) {
      console.warn('Unable to navigate to asset details: missing itemId', row);
      this.toasterService.error('Unable to view asset: missing item ID');
      return;
    }

    const typeLabel = (row.assetType || '').toLowerCase();
    let routeSegment: string | null = null;

    if (typeLabel === 'dataset') {
      routeSegment = 'dataset';
    } else if (typeLabel === 'model') {
      routeSegment = 'model';
    } else if (typeLabel === 'app') {
      routeSegment = 'usecase';
    } else {
      const rawType = asset.assetType || asset.asset_type || asset.type || original.type || '';
      const rawTypeStr = Array.isArray(rawType) ? (rawType[0] ?? '') : rawType;
      const rawTypeLower = (rawTypeStr + '').toLowerCase();

      if (rawTypeLower.includes('databank') || rawTypeLower.includes('database')) {
        routeSegment = 'dataset';
      } else if (rawTypeLower.includes('aimodel') || rawTypeLower.includes('model')) {
        routeSegment = 'model';
      } else if (rawTypeLower.includes('app') || rawTypeLower.includes('usecase')) {
        routeSegment = 'usecase';
      }
    }

    if (routeSegment) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/${routeSegment}`, itemId])
      );
      window.open(url, '_blank');
    } else {
      console.warn('Unknown asset type for navigation:', row.assetType, row);
      this.toasterService.error('Unable to determine asset type for navigation');
    }
  }

  isExpiryValid(): boolean {
    if (!this.expirationDate?.startDate) return false;
    const d = new Date(this.expirationDate.startDate);
    d.setHours(23, 59, 59, 999);
    return d.getTime() > Date.now();
  }

  async onExport(): Promise<void> {
    if (!this.showExportButton) return;
    try {
      await this.exportLoader.run(async () => {
        const response = await this.aclApdApiService.downloadProviderAccessRequestReport();
        if (!response) return;
        const blob =
          response instanceof Blob
            ? response
            : new Blob([response as unknown as string], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'asset-request-report.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Export failed:', error);
    }
  }

  private formatExpiryAt(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return istToUtc(`${year}-${month}-${day}`, '23:59:00');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

}


